import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { canScroll } from '@store/reducers/layout';

import Toolbar from '@components/configure/toolbar/toolbar';
import VehicleBanner from '@components/configure/banner/vehiclebanner';
import VehicleDetails from '@components/configure/details/vehicledetails';
import { ImageGalleryContainer } from '@components/configure/imagegallery';
import Loader from '@components/loader/loader';

import { useVehicle } from '@hooks/usevehicle';
import { useDeviceDetect } from '@hooks/usedevicedetect';
import { useAppDispatch, useAppSelector } from '@hooks/statehooks';
import { setInitialState } from '@store/reducers/selectedoptions';
import { useInitialOptions } from '@hooks/useinitialoptions';

import '@styles/page.scss';

import { Vehicle } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicle';
import Modal from '@components/modal/modal';
import useModal from '@hooks/usemodal';
import { ModalType } from '../../enums';

const Configure: React.FC = () => {
  const { loading, vehicleDetails, quoteValid } = useVehicle();
  const [showImageGallery, setShowImageGallery] = useState<boolean>(false);
  const { isVehicleQuotable } = useAppSelector(
    (state) => state.vehicleInformation
  );
  const { isMobile, isTablet } = useDeviceDetect();
  const dispatch = useAppDispatch();
  const modalSettings = useModal();

  useEffect(() => {
    window.history.scrollRestoration = 'manual'; // sets scroll to top
  }, []);

  useEffect(() => {
    dispatch(
      canScroll(!(showImageGallery && (isMobile || isTablet)) && !loading)
    );
  }, [showImageGallery, isMobile, isTablet, loading]);

  const onImageGallery = () => {
    setShowImageGallery(true);
    document.body.scrollIntoView({ behavior: 'smooth' });
  };

  const showInvalidQuoteModal = () => {
    modalSettings.setModalType(ModalType.invalidQuote);
    modalSettings.toggle();
  };

  const showNonQuotableModal = () => {
    modalSettings.setModalType(ModalType.nonQuotable);
    modalSettings.toggle();
  };

  const { finalisedOptions } = useAppSelector((state) => state);

  const syncSelectedOptions = () => {
    dispatch(
      setInitialState(
        useInitialOptions(vehicleDetails as Vehicle, finalisedOptions)
      )
    );
  };

  useEffect(() => {
    if (!loading) {
      syncSelectedOptions();
      if (!isVehicleQuotable) {
        showNonQuotableModal();
      } else if (!quoteValid) {
        showInvalidQuoteModal();
      }
    }
  }, [quoteValid, isVehicleQuotable, loading]);

  if (loading) {
    return <Loader className="vehicle-banner__loader" />;
  }

  return (
    <div className="page">
      <div className="page-configure">
        <div
          className={cn(
            'page-configure__header-container',
            isMobile &&
              showImageGallery &&
              'page-configure__header-container--mobile-open'
          )}
        >
          {showImageGallery && (
            <ImageGalleryContainer
              vehicle={vehicleDetails as Vehicle}
              onClose={() => setShowImageGallery(false)}
            ></ImageGalleryContainer>
          )}
          <div className="page-configure__header-container-toolbar">
            <Toolbar vehicle={vehicleDetails as Vehicle} />
          </div>
          <div className="page-configure__header-container-header">
            <VehicleBanner
              vehicle={vehicleDetails as Vehicle}
              onImageGallery={onImageGallery}
            />
          </div>
        </div>
        <div className="page-configure__details">
          <VehicleDetails vehicle={vehicleDetails as Vehicle} />
        </div>
        {(!quoteValid || !isVehicleQuotable) && (
          <Modal
            visible={modalSettings.visible}
            isClosable={modalSettings.modalType !== ModalType.nonQuotable}
            data-cy={`modal-${
              modalSettings.modalType === ModalType.nonQuotable
                ? 'nonquotable'
                : 'invalidquote'
            }`}
            content={modalSettings.content}
            toggle={modalSettings.toggle}
            modalType={modalSettings.modalType}
          />
        )}
      </div>
    </div>
  );
};

export default Configure;
